import { effect, inject, Injectable } from '@angular/core';
import { AddressService, AuthService } from '@nebalkon-workspace/auth';
import { catchError, EMPTY, finalize, of, switchMap, tap } from 'rxjs';
import {
  clearPendingPickupAddress,
  readPendingPickupAddress,
} from './pending-pickup-address.storage';
import { pickupAddressDraftMatchesSaved } from './pickup-address-match';

/**
 * После входа переносит адрес из localStorage (черновик заявки без аккаунта) в POST /addresses.
 * Если такой адрес уже есть у пользователя — только очищает черновик без POST.
 */
@Injectable({ providedIn: 'root' })
export class PendingPickupAddressSyncService {
  readonly #auth = inject(AuthService);
  readonly #addresses = inject(AddressService);
  #syncInFlight = false;

  constructor() {
    effect(() => {
      if (!this.#auth.isAuthenticated() || this.#syncInFlight) {
        return;
      }
      const draft = readPendingPickupAddress();
      if (!draft) {
        return;
      }
      this.#syncInFlight = true;
      this.#addresses
        .getAddresses()
        .pipe(
          catchError((err) => {
            console.error(
              'Failed to load addresses for pending pickup sync',
              err,
            );
            return EMPTY;
          }),
          switchMap((r) => {
            const list = r.addresses ?? [];
            if (list.some((a) => pickupAddressDraftMatchesSaved(draft, a))) {
              clearPendingPickupAddress();
              return of(null);
            }
            return this.#addresses
              .createAddress({
                city: draft.city,
                street: draft.street,
                building: draft.building,
                apartment: draft.apartment,
                entrance: draft.entrance,
                floor: draft.floor,
                intercom: draft.intercom,
              })
              .pipe(tap(() => clearPendingPickupAddress()));
          }),
          finalize(() => {
            this.#syncInFlight = false;
          }),
        )
        .subscribe({
          error: (err) => {
            console.error('Failed to sync pending pickup address', err);
          },
        });
    });
  }
}
